import { Icone } from '@/components/ui/Icone'
import { useMaintenance } from './etat'
import {
  delaiEnMots,
  FUSEAU_PAR_DEFAUT,
  heureDuCandidat,
  jourDuCandidat,
  memeJour,
  tonDeLAnnonce,
} from './heures'

/**
 * Le bandeau de maintenance — RG-9.10, RG-9.12, [DECISIONS §196].
 *
 * **Le ton se durcit, et le chiffre est TOUJOURS là.** C'est exactement le
 * raisonnement de `VerificationBanner` (RG-1.3) : « une maintenance est
 * prévue » ne dit rien de l'urgence, « dans 12 minutes » se comprend. Trois
 * tons, trois phrases, et un seul `alert` — celui de la fin, parce qu'alerter
 * six heures à l'avance apprend à ignorer les alertes suivantes.
 *
 * **Le bandeau porte AUSSI le « pendant », et c'est structurel** (RG-9.12).
 * Quand la fenêtre s'ouvre alors que l'application est déjà chargée, on ne
 * bascule PAS sur l'écran plein : il démonterait l'arbre React, donc l'épreuve
 * en cours, dont les réponses ne vivent qu'en mémoire (RG-3.11octies). Le
 * bandeau, lui, s'ajoute sans rien démonter — et c'est ce qui rend honnête la
 * phrase « ne ferme pas cet onglet ».
 *
 * **Après, rien** (RG-9.14). Le bandeau disparaît, et personne n'est remercié
 * de sa patience : un service rétabli est l'état normal.
 */
export function BandeauDeMaintenance({ fuseau = FUSEAU_PAR_DEFAUT }: { fuseau?: string }) {
  const maintenance = useMaintenance()

  // Celui qui répare travaille : lui poser un bandeau de fermeture au-dessus
  // du back-office qu'il est en train d'utiliser n'apprendrait rien à personne.
  if (maintenance.laissezPasser) return null

  if (maintenance.etat === 'en_cours') {
    return <EnCours maintenance={maintenance} fuseau={fuseau} />
  }

  if (maintenance.etat !== 'annoncee' || maintenance.debutA === null) return null

  return <Annonce maintenance={maintenance} fuseau={fuseau} />
}

type Etat = ReturnType<typeof useMaintenance>

/**
 * AVANT — trois tons, et le chiffre dans le titre à chaque fois.
 *
 * Chaque corps de texte dit une chose de plus que le précédent, et jamais
 * l'inverse : on informe, puis on conseille, puis on demande d'agir. Le
 * candidat qui ne lit que le titre a déjà l'essentiel.
 */
function Annonce({ maintenance, fuseau }: { maintenance: Etat; fuseau: string }) {
  const restant = maintenance.restantMs ?? 0
  const ton = tonDeLAnnonce(restant)
  const urgent = ton === 'urgent'

  const debut = maintenance.debutA
  const reprise = maintenance.repriseA

  if (debut === null) return null

  const heureDebut = heureDuCandidat(debut, fuseau)
  const heureFin = reprise === null ? null : heureDuCandidat(reprise, fuseau)

  // « le samedi 16 août, de 2h00 à 2h30 » — le jour n'apparaît que s'il n'est
  // pas aujourd'hui : « aujourd'hui de 2h00 à 2h30 » se dit sans la date.
  const quand = memeJour(debut, new Date().toISOString(), fuseau)
    ? `de ${heureDebut}`
    : `le ${jourDuCandidat(debut, fuseau)}, de ${heureDebut}`

  const fenetre = heureFin === null ? quand : `${quand} à ${heureFin}`

  return (
    <section
      // `alert` seulement à l'approche — voir l'en-tête du fichier.
      role={urgent ? 'alert' : 'status'}
      className="flex flex-col gap-2 border-b p-4"
      style={{ borderColor: 'var(--bordure)', background: 'var(--surface-2)' }}
    >
      <h2 className="flex items-center gap-2 text-[15px] font-bold">
        <Icone nom="horloge" taille={16} />
        {restant <= 0
          ? 'Maintenance imminente'
          : `Maintenance dans ${delaiEnMots(restant)}`}
      </h2>

      <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        {ton === 'sobre' && (
          <>
            L’application sera fermée {fenetre}, heure d’Haïti.
            {maintenance.motif === null ? '' : ` Motif : ${minuscule(maintenance.motif)}.`} Tout ce
            que tu as déjà envoyé est enregistré.
          </>
        )}
        {ton === 'precis' && (
          <>
            L’application ferme à {heureDebut}
            {heureFin === null ? '' : ` et revient à ${heureFin}`}, heure d’Haïti. Ne commence pas
            une épreuve que tu ne finiras pas avant : tant qu’elle n’est pas remise, rien n’a quitté
            ton appareil.
          </>
        )}
        {urgent && (
          <>
            Remets maintenant l’épreuve que tu as commencée : à {heureDebut} l’application s’arrête,
            et une copie non remise est perdue. Ton compte, ta progression et ta série, eux, ne
            bougent pas.
          </>
        )}
      </p>
    </section>
  )
}

/**
 * PENDANT, pour une session déjà ouverte — RG-9.12.
 *
 * **La phrase qui compte est « ne ferme pas cet onglet ».** Elle n'est vraie
 * que parce que ce composant est un bandeau et non un écran : rien n'a été
 * démonté, l'épreuve est toujours là, et elle se remettra à la reprise.
 */
function EnCours({ maintenance, fuseau }: { maintenance: Etat; fuseau: string }) {
  const reprise = maintenance.repriseA

  return (
    <section
      role="alert"
      className="flex flex-col gap-2 border-b p-4"
      style={{ borderColor: 'var(--bordure)', background: 'var(--surface-2)' }}
    >
      <h2 className="flex items-center gap-2 text-[15px] font-bold">
        <Icone nom="horloge" taille={16} />
        La maintenance a commencé — l’application ne répond plus
      </h2>

      <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        {reprise !== null && !maintenance.enRetard && (
          <>Elle revient à {heureDuCandidat(reprise, fuseau)}, heure d’Haïti. </>
        )}
        {reprise !== null && maintenance.enRetard && (
          <>
            La reprise était prévue à {heureDuCandidat(reprise, fuseau)} ; elle a du retard.{' '}
          </>
        )}
        {reprise === null && <>L’heure de reprise n’a pas été annoncée. </>}
        <strong>Ne ferme pas cet onglet</strong> : une épreuve commencée n’a rien envoyé, et tu
        pourras la remettre à la reprise. Tout ce qui était déjà parti est enregistré.
      </p>
    </section>
  )
}

/**
 * Le motif est saisi par un administrateur : il commence souvent par une
 * majuscule, et se retrouve ici au milieu d'une phrase.
 */
function minuscule(motif: string): string {
  return motif.charAt(0).toLowerCase() + motif.slice(1)
}
